import {
  BellRingIcon,
  CheckIcon,
  DownloadIcon,
  InfoIcon,
  LanguagesIcon,
  LogOutIcon,
  MonitorIcon,
  MoonIcon,
  SunIcon,
  UserRoundIcon,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import type { ThemePreference } from '@/shared/api/types';
import { useCurrentUser } from '@/shared/auth/hooks';
import { changeLanguage, changeTheme } from '@/shared/auth/preferences';
import { Avatar } from '@/shared/components/avatar';
import { LANGUAGES, SUPPORTED_LANGUAGES, toSupportedLanguage, type Language } from '@/shared/i18n/languages';
import { useMediaQuery } from '@/shared/lib/hooks/useMediaQuery';
import { promptInstall, useInstallAvailability } from '@/shared/pwa/install';
import { useThemeStore } from '@/shared/theme/store';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';

import { AboutDialog } from './AboutDialog';
import { InstallAppDialog } from './InstallAppDialog';
import { signOut } from './signOut';

const THEME_ICONS = { light: SunIcon, dark: MoonIcon, system: MonitorIcon } as const;
const THEMES = ['light', 'dark', 'system'] as const;

function ThemeItems({ theme }: { theme: ThemePreference }) {
  const { t } = useTranslation('app');
  return (
    <DropdownMenuRadioGroup value={theme} onValueChange={(value) => changeTheme(value as ThemePreference)}>
      {THEMES.map((option) => {
        const Icon = THEME_ICONS[option];
        return (
          <DropdownMenuRadioItem key={option} value={option} data-testid={`theme-${option}`}>
            <Icon className="text-muted-foreground" />
            {t(`theme.${option}`)}
          </DropdownMenuRadioItem>
        );
      })}
    </DropdownMenuRadioGroup>
  );
}

/**
 * Theme and language as submenus; on phones (no room beside the menu, a submenu would open off-screen) as radio
 * groups inside the menu.
 */
function PreferenceItems({ theme, language }: { theme: ThemePreference; language: Language }) {
  const { t } = useTranslation('app');
  const inline = useMediaQuery('(max-width: 639px)');
  const ThemeIcon = THEME_ICONS[theme];

  if (inline) {
    return (
      <>
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-xs font-medium text-muted-foreground">
          {t('userMenu.theme')}
        </DropdownMenuLabel>
        <ThemeItems theme={theme} />
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-xs font-medium text-muted-foreground">
          {t('userMenu.language')}
        </DropdownMenuLabel>
        <DropdownMenuRadioGroup value={language} onValueChange={(value) => changeLanguage(value as Language)}>
          {SUPPORTED_LANGUAGES.map((lng) => (
            <DropdownMenuRadioItem key={lng} value={lng} data-testid={`language-${lng}`}>
              <span className="w-6 text-xs font-medium text-muted-foreground uppercase">{lng}</span>
              {LANGUAGES[lng].nativeName}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </>
    );
  }

  return (
    <>
      <DropdownMenuSub>
        <DropdownMenuSubTrigger data-testid="theme-menu">
          <ThemeIcon />
          {t('userMenu.theme')}
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent>
          <ThemeItems theme={theme} />
        </DropdownMenuSubContent>
      </DropdownMenuSub>
      <DropdownMenuSub>
        <DropdownMenuSubTrigger data-testid="language-menu">
          <LanguagesIcon />
          {t('userMenu.language')}
          <span className="ml-auto text-xs text-muted-foreground uppercase">{language}</span>
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent>
          {SUPPORTED_LANGUAGES.map((lng) => (
            <DropdownMenuItem key={lng} onSelect={() => changeLanguage(lng)} data-testid={`language-${lng}`}>
              <span className="w-6 text-xs font-medium text-muted-foreground uppercase">{lng}</span>
              {LANGUAGES[lng].nativeName}
              {lng === language && <CheckIcon className="ml-auto size-4 text-brand-text" />}
            </DropdownMenuItem>
          ))}
        </DropdownMenuSubContent>
      </DropdownMenuSub>
    </>
  );
}

export function UserMenu() {
  const { t, i18n } = useTranslation('app');
  const user = useCurrentUser();
  const theme = useThemeStore((s) => s.theme);
  const language = toSupportedLanguage(i18n.language) ?? 'ru';
  const [aboutOpen, setAboutOpen] = useState(false);
  const [installOpen, setInstallOpen] = useState(false);
  const installation = useInstallAvailability();

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="rounded-full focus-ring transition-opacity hover:opacity-90"
            aria-label={t('userMenu.open')}
            data-testid="user-menu-trigger"
          >
            <Avatar user={{ ...user, isOnline: true }} size="md" showPresence />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          <DropdownMenuLabel className="flex items-center gap-3 px-2 py-2 font-normal">
            <Avatar user={user} size="lg" />
            <span className="min-w-0">
              <span className="block truncate text-sm font-semibold text-foreground">{user.fullName}</span>
              <span className="block truncate text-xs text-muted-foreground">{user.email}</span>
            </span>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuGroup>
            <DropdownMenuItem asChild>
              <Link to={`/company/users/${user.id}`}>
                <UserRoundIcon />
                {t('userMenu.profile')}
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/notifications/settings" data-testid="user-menu-notification-settings">
                <BellRingIcon />
                {t('userMenu.notificationSettings')}
              </Link>
            </DropdownMenuItem>
            <PreferenceItems theme={theme} language={language} />
          </DropdownMenuGroup>
          <DropdownMenuSeparator />
          {(installation === 'prompt' || installation === 'ios') && (
            <DropdownMenuItem
              onSelect={() => (installation === 'prompt' ? void promptInstall() : setInstallOpen(true))}
              data-testid="user-menu-install"
            >
              <DownloadIcon />
              {t('userMenu.install')}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onSelect={() => setAboutOpen(true)} data-testid="user-menu-about">
            <InfoIcon />
            {t('userMenu.about')}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={signOut} data-testid="logout">
            <LogOutIcon />
            {t('userMenu.logout')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <AboutDialog open={aboutOpen} onOpenChange={setAboutOpen} />
      <InstallAppDialog open={installOpen} onOpenChange={setInstallOpen} />
    </>
  );
}
